import type { TransportResponse } from '@mangovoice/api-client'

/**
 * Error thrown when the API returns a 4xx with field-keyed validation errors.
 * Enables showing errors next to the relevant form fields.
 */
export class AiConfigValidationError extends Error {
  status?: number
  data?: unknown

  constructor(
    message: string,
    public readonly fields: Record<string, string>,
  ) {
    super(message)
    this.name = 'AiConfigValidationError'
  }
}

function getBackendErrorMessage(data: unknown): string | null {
  if (data == null) return null
  if (typeof data === 'string') return data
  if (typeof data !== 'object') return null
  const obj = data as Record<string, unknown>
  if (typeof obj.message === 'string') return obj.message
  if (typeof obj.detail === 'string') return obj.detail
  if (Array.isArray(obj.errors) && obj.errors.length > 0) {
    const first = obj.errors[0]
    if (typeof first === 'string') return first
    if (typeof first === 'object' && first !== null && 'message' in first)
      return String((first as { message: unknown }).message)
  }
  for (const value of Object.values(obj)) {
    if (typeof value === 'string' && value.trim() !== '') return value
  }
  return null
}

function getBackendFieldErrors(data: unknown): Record<string, string> | null {
  if (data == null || typeof data !== 'object') return null
  const obj = data as Record<string, unknown>
  if (typeof obj.message === 'string' || typeof obj.detail === 'string')
    return null
  if (Array.isArray(obj.errors) && obj.errors.length > 0) return null
  const fields: Record<string, string> = {}
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'string' && value.trim() !== '')
      fields[key] = value.trim()
  }
  return Object.keys(fields).length > 0 ? fields : null
}

// Errors thrown by unwrapResponse carry the HTTP status and raw body so
// callers can branch on structured fields the message alone flattens away
// (e.g. the running jobId on a scrape-job 409).
export interface AiConfigRequestError extends Error {
  status?: number
  data?: unknown
}

const toRequestError = (
  message: string,
  response: TransportResponse<unknown>,
): AiConfigRequestError => {
  const err = new Error(message) as AiConfigRequestError
  err.status = response.status
  err.data = response.data
  return err
}

export const unwrapResponse = <T>(response: TransportResponse<T>): T => {
  if (!response.ok) {
    const backendMessage = getBackendErrorMessage(response.data)
    const message =
      backendMessage ??
      (response.status
        ? `API request failed with status ${response.status}`
        : `API request failed`)
    const fieldErrors = getBackendFieldErrors(response.data)
    if (fieldErrors && Object.keys(fieldErrors).length > 0) {
      const validation = new AiConfigValidationError(message, fieldErrors)
      validation.status = response.status
      validation.data = response.data
      throw validation
    }
    throw toRequestError(message, response)
  }
  return response.data as T
}
